import { Suspense, useContext, useMemo } from 'react';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import {
    CheckoutPageData,
    CheckoutPageVariables,
    getCheckoutPageConfig,
} from 'src/modules/checkout/CheckoutPageMapping.ts';
import { ConfigContext } from 'src/configContext.ts';
import { LocationState } from 'src/modules/LocationState.ts';
import { getFilterValuesFromUrl } from 'src/utils/getFilterValuesFromUrl.ts';
import { getInitialState, initInputState } from 'src/modules/user-input/InitInputState.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { targetUnits, targetUnitsAm } from 'src/modules/user-input/targetUnits.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { buildCheckoutVariables } from 'src/modules/checkout/buildCheckoutVariables.ts';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';

type FetchValveSelectionProps = {
    locationState: LocationState;
    searchParams: string;
}

export const FetchValveSelection = ({ locationState, searchParams }: FetchValveSelectionProps) => {
    const {
        valveLabel,
        actuatorLabel,
    } = locationState;

    const { userSettings } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getCheckoutPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const initialState = getInitialState(userSettings.currentRegion === RegionsEnum.Am ? targetUnitsAm : targetUnits);

    const userInput = useMemo(() => initInputState(
        new URLSearchParams(searchParams),
        initialState,
    ), [initialState, searchParams]);

    const filter = getFilterValuesFromUrl(new URLSearchParams(searchParams), [
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...pageConfig.actuatorsFilterConfig,
    ]);

    const variables = useMemo(() => buildCheckoutVariables({
        filter,
        actuatorLabel,
        valveLabel,
        userSettings,
        userInput,
    }), [
        filter,
        actuatorLabel,
        valveLabel,
        userSettings,
        userInput,
    ]);

    return (
        <Suspense fallback={<SkeletonSelection/>}>
            <Query<CheckoutPageData, CheckoutPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return 'Loading...';
                    }

                    const { valve } = data.vssAccessories;

                    const comboCurrent = locationState.combo;

                    const comboCandidate = data.vssCombo
                        .find((item) => item.Label.includes(comboCurrent && comboCurrent.mounted ? '+' : '/'));
                    const combo = comboCandidate ?? valve;

                    const { isRecommended } = parseRecommended(valve.connectors);

                    const valveAccessories = prepareAccessories<
                    AccessoriesPageData['vssAccessories']['valveAccessories']['0']
                    >(
                        [
                            ...data.vssAccessories.valveAccessories,
                            ...data.vssAccessories.actuatorAccessories || [],
                        ],
                        locationState.selectedAccessories,
                        userSettings,
                        null,
                        (a, b) => sortAccessoriesBy(isRecommended)(a.accessory, b.accessory),
                    );

                    const pipeConnectors = valveAccessories
                        .filter(({ accessory }) => isRecommended(accessory.item.Label))
                        .map((connector) => ({ ...connector, qty: connector.qty }));

                    const accessories = valveAccessories
                        .filter(({ accessory }) => !isRecommended(accessory.item.Label))
                        .map((connector) => ({ ...connector, qty: connector.qty }));

                    return (
                        <>
                            <ProjectSelectionDeviceList
                                title={<I18n messageId={MESSAGES.checkoutValveAndActuatorTitle}/>}
                            >
                                <ProjectSelectionDevice
                                    productImage={(
                                        <ProductImage
                                            imgSrc={combo.image?.raw.join('') || ''}
                                        >
                                            <ValveIcon/>
                                        </ProductImage>
                                    )}
                                    productLink={(
                                        <ViewProductLink
                                            userSettings={userSettings}
                                            code={combo.Label}
                                            content=""
                                        >
                                            <span className="vss-device__product-label">
                                                {combo.Label}
                                            </span>

                                            <div className="vss-device__product-icon">
                                                <ArrowRightIcon/>
                                            </div>
                                        </ViewProductLink>
                                    )}
                                    productDescription={(
                                        <RenderHtml
                                            data={combo.productType?.localized.join('') || ''}
                                        />
                                    )}
                                    quantity={comboCurrent?.qty || 1}
                                />
                            </ProjectSelectionDeviceList>

                            {pipeConnectors.length ? (
                                <ProjectSelectionDeviceList
                                    title={<I18n messageId={MESSAGES.PipeConnectors}/>}
                                >
                                    {pipeConnectors.map(({ accessory: { item }, qty }) => (
                                        <ProjectSelectionDevice
                                            key={item.Label}
                                            productImage={(
                                                <ProductImage
                                                    imgSrc={item.image?.raw.join('') || ''}
                                                >
                                                    <ActuatorIcon/>
                                                </ProductImage>
                                            )}
                                            productLink={(
                                                <ViewProductLink
                                                    userSettings={userSettings}
                                                    code={item.Label}
                                                    content=""
                                                >
                                                    {item.Label}

                                                    <div className="vss-accessory__product-icon">
                                                        <ArrowRightIcon/>
                                                    </div>
                                                </ViewProductLink>
                                            )}
                                            productDescription={(
                                                <RenderHtml
                                                    data={item.accessoryType?.localized.join('') || ''}
                                                />
                                            )}
                                            quantity={qty}
                                        />

                                    ))}
                                </ProjectSelectionDeviceList>
                            ) : null}

                            {accessories.length ? (
                                <ProjectSelectionDeviceList
                                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                                >
                                    {accessories.map(({ accessory: { item }, qty }) => (
                                        <ProjectSelectionDevice
                                            key={item.Label}
                                            productImage={(
                                                <ProductImage
                                                    imgSrc={item.image?.raw.join('') || ''}
                                                >
                                                    <ActuatorIcon/>
                                                </ProductImage>
                                            )}
                                            productLink={(
                                                <ViewProductLink
                                                    userSettings={userSettings}
                                                    code={item.Label}
                                                    content=""
                                                >
                                                    {item.Label}

                                                    <div className="vss-accessory__product-icon">
                                                        <ArrowRightIcon/>
                                                    </div>
                                                </ViewProductLink>
                                            )}
                                            productDescription={(
                                                <RenderHtml
                                                    data={item.accessoryType?.localized.join('') || ''}
                                                />
                                            )}
                                            quantity={qty}
                                        />
                                    ))}
                                </ProjectSelectionDeviceList>
                            ) : null}
                        </>
                    );
                }}
            </Query>
        </Suspense>
    );
};
